import React, { useEffect, useMemo, useRef, useState } from "react";
import { Icon, resolveIconSize, type IconSize } from "@/components/Icon";
import { getAttachmentFileIcon } from "@/utils/attachmentFileIcon";
import { formatFileSize } from "@/utils/format";

interface AttachmentThumbnailProps {
    name: string;
    mimeType?: string;
    fileSize?: number;
    load: () => Promise<Blob>;
    onClick: () => void;
    className?: string;
    inverted?: boolean;
    size?: number | IconSize;
    overlayImageName?: boolean;
}

export const AttachmentThumbnail: React.FC<AttachmentThumbnailProps> = ({
    name,
    mimeType,
    fileSize,
    load,
    onClick,
    className = "",
    inverted = false,
    size = "md",
    overlayImageName = false,
}) => {
    const kind = useMemo(
        () => getAttachmentFileIcon(name, mimeType),
        [name, mimeType]
    );
    const [url, setUrl] = useState<string | null>(null);
    const loadRef = useRef(load);
    loadRef.current = load;
    const sizeLabel = formatFileSize(fileSize);
    const iconSize = resolveIconSize(size);
    const imgSize = `${iconSize}px`;

    useEffect(() => {
        if (!kind.isImage) {
            return;
        }
        let active = true;
        let objectUrl = "";
        loadRef
            .current()
            .then((blob) => {
                if (!active) {
                    return;
                }
                objectUrl = URL.createObjectURL(blob);
                setUrl(objectUrl);
            })
            .catch(() => undefined);
        return () => {
            active = false;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [kind.isImage, name]);

    return (
        <button
            type="button"
            onClick={onClick}
            className={`${overlayImageName && kind.isImage ? "relative h-24 w-36 p-0" : "flex w-full min-w-0 items-center gap-1 px-2 py-1.5"} overflow-hidden rounded-lg border text-left transition-colors outline-none focus:outline-none focus-visible:outline-none ring-0 focus:ring-0 focus-visible:ring-0 ${
                inverted
                    ? "border-white/20 bg-white/20 text-white hover:bg-white/30 hover:border-white/40"
                    : "border-current/15 bg-white/60 hover:border-primary/50 hover:bg-white/80 dark:bg-black/20 dark:hover:bg-black/30"
            } ${className}`}
            title={name}
        >
            {kind.isImage && url ? (
                <img
                    src={url}
                    alt={name}
                    className={`${overlayImageName ? "absolute inset-0 h-full w-full" : "shrink-0"} rounded object-cover`}
                    style={overlayImageName ? undefined : { width: imgSize, height: imgSize }}
                />
            ) : (
                <span
                    className={`shrink-0 ${inverted ? "text-white" : kind.color}`}
                >
                    <Icon value={kind.icon} size={iconSize} />
                </span>
            )}
            <span className={`${overlayImageName && kind.isImage ? "absolute inset-x-0 bottom-0 bg-black/65 px-2 py-1 text-center text-white" : "min-w-0 flex-1"} truncate text-[11px] font-medium leading-none`}>
                {name}
            </span>
            {sizeLabel && !(overlayImageName && kind.isImage) && (
                <span
                    className={`shrink-0 text-[10px] font-normal tabular-nums ${inverted ? "text-white/70" : "text-slate-400 dark:text-zinc-500"}`}
                >
                    {sizeLabel}
                </span>
            )}
        </button>
    );
};
